import { Overlay, OverlayConfig } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { ComponentRef, computed, inject, Injectable, Injector, Signal, signal } from '@angular/core'
import { ResolveEnd, Router } from '@angular/router'
import { takeUntil } from 'rxjs'

import { WindowRef } from 'app/core/window-ref.service'
import { filterIfInstanceOf } from 'app/utils/rxjs/filter-if-instance-of.operator'

import { MobileNavComponent } from './mobile-nav.component'
import { SideNavRef } from './side-nav-ref'

@Injectable({ providedIn: 'root' })
export class SideNavService {
  readonly isOpen: Signal<boolean>

  private ref: SideNavRef | null = null
  private readonly navState = signal<'closed' | 'open'>('closed')
  private readonly overlay = inject(Overlay)
  private readonly winRef = inject(WindowRef)
  private readonly router = inject(Router)

  constructor() {
    this.isOpen = computed(() => this.navState() === 'open')
  }

  readonly toggle = (injector: Injector) => {
    return this.isOpen() ? this.close() : this.open(injector)
  }

  readonly close = () => {
    this.ref?.close()
    this.ref = null
    this.navState.set('closed')
  }

  private readonly open = (injector: Injector) => {
    const overlayRef = this.overlay.create(this.getOverlayConfig())
    const containerPortal = new ComponentPortal(MobileNavComponent, null, injector)
    const componentRef: ComponentRef<MobileNavComponent> = overlayRef.attach(containerPortal)
    this.ref = new SideNavRef(overlayRef, componentRef.instance)
    overlayRef.backdropClick().subscribe(this.close)
    // navigation end is too late to have the body scrolled correctly. so we need ResolveEnd
    this.router.events.pipe(takeUntil(this.ref.close$), filterIfInstanceOf(ResolveEnd)).subscribe(this.close)
    this.navState.set('open')
  }

  private getOverlayConfig(): OverlayConfig {
    const positionStrategy = this.overlay.position().global().left('0')

    return new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'pai-sidenav-backdrop',
      panelClass: 'pai-sidenav-panel',
      width: '100%',
      height: this.winRef.nativeWindow?.innerHeight,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
    })
  }
}
